import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DC] p-8">
          {success ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="text-xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Password Reset Successfully</h2>
              <p className="text-sm text-[#6B4E37]">Redirecting to login...</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#1A2F4A] text-center mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="reset-title">
                Reset Password
              </h1>
              <p className="text-sm text-[#6B4E37] text-center mb-6">Enter your new password below.</p>

              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">New Password</label>
                  <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required placeholder="At least 6 characters"
                    className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8] pr-12"
                    data-testid="reset-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-9 text-[#999] hover:text-[#1A2F4A]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Confirm New Password</label>
                  <input type="password" value={form.confirm} onChange={set('confirm')} required placeholder="Re-enter your new password"
                    className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                    data-testid="reset-confirm" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-60"
                  data-testid="reset-submit">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
